import { useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {BrowserRouter} from "react-router-dom";
import {initializeI18nAndThenAppendCustomerSpecificStylesheet, clearLocalStorageForUser} from "./lib/Utils";
import RouterComponent from "./components/RouterComponent";
import ConnectionLost from "./components/ConnectionLost";
import { ThemeProvider,createTheme } from "@mui/material/styles";
import CssBaseLine from '@mui/material/CssBaseline';
import { APP_DARK_PURPLE_COLOR, APP_ENGLISH_VIOLET_COLOR, APP_WHITE_COLOR } from "./assets/constants/Colors";
// import { useMediaQuery } from "@mui/material";
import moment from "moment";
import { IS_LOGGED_IN, LOCAL_STORAGE, REFRESH_TOKEN_EXPIRY_TIME } from "./assets/constants/BrowserStorageKeys";
import { getSecureItemFromSpecificStorage } from "./lib/BrowserStorageAccessMiddleware";
import ShowToast from "./components/ToastComponent";
import { DARK, LIGHT, TOAST_ERROR } from "./assets/constants/Constants";
import CircularProgressLoader from "./components/CircularProgressLoader";

initializeI18nAndThenAppendCustomerSpecificStylesheet();

const App = () =>  {
  const [isOffline, setIsOffline] = useState( !navigator.onLine );
  const prefersDarkMode = false; //useMediaQuery('(prefers-color-scheme: dark)');
  const [showLoader, setShowLoader] = useState(false);
  const { t } = useTranslation();

  const handleOnlineStatus = () => setIsOffline(false);
  const handleOfflineStatus = () => setIsOffline(true);

  const handleBackButton = (event) => {
    const currentPage = window.location.pathname;
    if (currentPage === '/locker-size') {
      window.location.reload();
    }
  };

  useEffect(() => {
    window.addEventListener('popstate', handleBackButton);
    window.addEventListener('online', handleOnlineStatus);
    window.addEventListener('offline', handleOfflineStatus);
    window.addEventListener('click', checkLoginAndTokenExpiry);

    return () => { // Cleanup function for event listeners
      window.removeEventListener('popstate', handleBackButton);
      window.removeEventListener('online', handleOnlineStatus);
      window.removeEventListener('offline', handleOfflineStatus);
      window.removeEventListener('click', checkLoginAndTokenExpiry);
    };
  }, []); // Empty dependency array to run effect only once

  const getCSSVariable = (variableName, fallbackValue) => `var(${variableName}, ${fallbackValue})`;

  const theme = createTheme({
    palette: {
      mode: prefersDarkMode ? DARK : LIGHT, // Handle dark mode state on toggle
      background: {
        default: prefersDarkMode ? APP_DARK_PURPLE_COLOR : APP_WHITE_COLOR,
        primary: prefersDarkMode ? APP_ENGLISH_VIOLET_COLOR : APP_WHITE_COLOR,

      },
      text : {
        primary: prefersDarkMode ? APP_WHITE_COLOR : APP_DARK_PURPLE_COLOR, 
        secondary : prefersDarkMode ? APP_DARK_PURPLE_COLOR : APP_WHITE_COLOR
      },
    },
    components: {
      MuiTypography: {
        styleOverrides: {
          h1: {
            fontFamily: getCSSVariable('--font-family-heading-1', 'Manrope'),
            fontSize: getCSSVariable('--font-size-heading-1-regular', '32px'),
            fontWeight: getCSSVariable('--font-weight-semibold', 600),
            color: getCSSVariable('--text-color-heading-1', '#2F2F2F')
          },
          h2: {
            fontFamily: getCSSVariable('--font-family-heading-2', 'Manrope'),
            fontSize: getCSSVariable('--font-size-heading-2-regular', '24px'),
            fontWeight: getCSSVariable('--font-weight-semibold', 600),
            color: getCSSVariable('--text-color-heading-2', '#2F2F2F')
          },
          h3: {
            fontFamily: getCSSVariable('--font-family-heading-3', 'Manrope'),
            fontSize: getCSSVariable('--font-size-heading-3-regular', '18px'),
            fontWeight: getCSSVariable('--font-weight-semibold', 600),
            color: getCSSVariable('--text-color-heading-3', '#2F2F2F')
          },
          h4: {
            fontFamily: getCSSVariable('--font-family-heading-4', 'Manrope'),
            fontSize: getCSSVariable('--font-size-heading-4-regular', '16px'),
            fontWeight: getCSSVariable('--font-weight-semibold', 600),
            color: getCSSVariable('--text-color-heading-4', '#2F2F2F')
          },
          h5: {
            fontFamily: getCSSVariable('--font-family-heading-5', 'Manrope'),
            fontSize: getCSSVariable('--font-size-heading-5-regular', '14px'),
            fontWeight: getCSSVariable('--font-weight-semibold', 600),
            color: getCSSVariable('--text-color-heading-5', '#2F2F2F')
          },
          h6: {
            fontFamily: getCSSVariable('--font-family-heading-6', 'Manrope'),
            fontSize: getCSSVariable('--font-size-heading-6-regular', '12px'),
            fontWeight: getCSSVariable('--font-weight-semibold', 600),
            color: getCSSVariable('--text-color-heading-6', '#2F2F2F')
          },
          body1: {
            fontFamily: getCSSVariable('--font-family-body-text', 'Manrope'),
            fontSize: getCSSVariable('--font-size-body-text-regular', '14px'),
            fontWeight: getCSSVariable('--font-weight-regular', 400),
            color: getCSSVariable('--text-color-body-text-regular', '#2F2F2F')
          },
          body2: {
            fontFamily: getCSSVariable('--font-family-body-text', 'Manrope'),
            fontSize: getCSSVariable('--font-size-body-text-regular', '12px'),
            fontWeight: getCSSVariable('--font-weight-regular', 400),
            color: getCSSVariable('--text-color-body-text-small', '#2F2F2F')
          }
        }
      },
      MuiButton: {
        styleOverrides: {
          root: {
            padding: getCSSVariable('--btn-padding', '10px 10px'),
            borderRadius: getCSSVariable('--btn-border-radius', 0),
            fontFamily: getCSSVariable('--btn-font-family', 'Manrope'),
            fontSize: getCSSVariable('--btn-font-size', 400),
            boxShadow: 'none',
            '&:hover': {
              boxShadow: 'none'
            },
            '&.primary': {
              border: getCSSVariable('--btn-primary-border', '1px solid #90C242'),
              background: getCSSVariable('--btn-primary-background', '#90C242'),
              color: getCSSVariable('--btn-primary-text-color', '#F2F4F3'),
              '&:hover': {
                border: getCSSVariable('--btn-primary-border', '1px solid #90C242'),
                background: getCSSVariable('--btn-primary-background', '#90C242'),
                color: getCSSVariable('--btn-primary-text-color', '#F2F4F3')
              }
            },
            '&.secondary': {
              border: getCSSVariable('--btn-secondary-border', '1px solid #90C242'),
              background: getCSSVariable('--btn-secondary-background', '#F2F4F3'),
              color: getCSSVariable('--btn-secondary-text-color', '#90C242'),
              '&:hover': {
                border: getCSSVariable('--btn-secondary-border', '1px solid #90C242'),
                background: getCSSVariable('--btn-secondary-background', '#F2F4F3'),
                color: getCSSVariable('--btn-secondary-text-color', '#90C242')
              }
            },
            '&.warning': {
              border: getCSSVariable('--btn-warning-border', '1px solid #FF5154'),
              background: getCSSVariable('--btn-warning-background', '#FF5154'),
              color: getCSSVariable('--btn-warning-text-color', '#F2F4F3'),
              '&:hover': {
                border: getCSSVariable('--btn-warning-border', '1px solid #FF5154'),
                background: getCSSVariable('--btn-warning-background', '#FF5154'),
                color: getCSSVariable('--btn-warning-text-color', '#F2F4F3')
              }
            },
            '&.disabled-button': {
              border: getCSSVariable('--btn-disabled-border', '1px solid #E9F4D3'),
              background: getCSSVariable('--btn-disabled-background', '#E9F4D3'),
              color: getCSSVariable('--btn-disabled-text-color', '#90C242'),
              '&:hover': {
                border: getCSSVariable('--btn-disabled-border', '1px solid #E9F4D3'),
                background: getCSSVariable('--btn-disabled-background', '#E9F4D3'),
                color: getCSSVariable('--btn-disabled-text-color', '#90C242')
              }
            }
          }
        }
      }
    }
  });

  const checkLoginAndTokenExpiry = async () => {
    const isLoggedIn = getSecureItemFromSpecificStorage(LOCAL_STORAGE,IS_LOGGED_IN);
    const refreshExpiryAt = await getSecureItemFromSpecificStorage(LOCAL_STORAGE, REFRESH_TOKEN_EXPIRY_TIME);
    const now = moment.utc();
    const refreshTokenExpiryTime = moment.utc(refreshExpiryAt);
  
    if (isLoggedIn && now.isAfter(refreshTokenExpiryTime)) {
        clearLocalStorageForUser();
        ShowToast(t('sessionExpired'), TOAST_ERROR);
        setShowLoader(true);
        setTimeout(() => {
            setShowLoader(false);
            window.location.href = '/';
        }, 3000); // Adjust the delay as necessary (3000ms = 3 seconds)
    }
  };
  

  return (
    <ThemeProvider theme={theme}>
      <CssBaseLine />
      <BrowserRouter>
        <RouterComponent theme={theme} />
      </BrowserRouter>
      { isOffline && <ConnectionLost /> }
      {showLoader && <CircularProgressLoader message={t('redirectToLogin')} />}
    </ThemeProvider>
  );
}

export default App;

